import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
// @ts-ignore
import DHBox from "@digital-hig/mui/Box";
// @ts-ignore
import DHTypography from "@digital-hig/mui/Typography";
// @ts-ignore
import DHButton from "@digital-hig/mui/Button";
// @ts-ignore
import DHCard from "@digital-hig/mui/Card";
// @ts-ignore
import DHCardContent from "@digital-hig/mui/CardContent";
// @ts-ignore
import DHCircularProgress from "@digital-hig/mui/CircularProgress";
import { useCfpAuthUtilities } from '../providers/CfpAuthContext';
import { getUpdatedConfig } from '../config/env';

export default function SignInPage() {

  const { cfpAuthUtils, loading } = useCfpAuthUtilities();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Print all environment variables for debugging
  useEffect(() => {
    logger.debug('=== Environment Variables ===');
    logger.debug('REACT_APP_APS_CLIENT_ID:', process.env.REACT_APP_APS_CLIENT_ID);
    logger.debug('REACT_APP_PROCESS_SETTING_ENDPOINT:', process.env.REACT_APP_PROCESS_SETTING_ENDPOINT);
    logger.debug('REACT_APP_AUTH_USER_ENDPOINT:', process.env.REACT_APP_AUTH_USER_ENDPOINT);
    logger.debug('REACT_APP_ENV:', process.env.REACT_APP_ENV);
    logger.debug('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    logger.debug('===============================');
  }, []);

  const handleLogin = async () => {
    setIsAuthenticating(true); // Set loading immediately when button is clicked
    try {
      await cfpAuthUtils.initialize(getUpdatedConfig());
      await cfpAuthUtils.login();
    } catch (error) {
      logger.error('Login failed:', error);
    } finally {
      setIsAuthenticating(false); // Reset loading state
    }
  };

  return (
    <DHBox
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/images/autodesk-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        // Add overlay for better text readability
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1,
        },
        // Ensure content is above overlay
        '& > *': {
          position: 'relative',
          zIndex: 2,
        }
      }}
    >
      <DHCard
        sx={{
          width: 560,
          height: 316.6308898925781,
          position: 'absolute',
          top: 128,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          opacity: 1,
        }}
      >
        <DHCardContent sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Top Row - Autodesk Logo */}
          <DHBox 
            sx={{ 
              height: 81,
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              gap: '10px'
            }}
          >
            <DHBox 
              sx={{ 
                width: 496,
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}
            >
              <img 
                src="/images/autodesk-black.png" 
                alt="Autodesk"
                style={{
                  width: '162px',
                  height: '17px',
                  objectFit: 'contain',
                  opacity: 1,
                }}
              />
            </DHBox>
          </DHBox>

          {/* Second Row */}
          <DHBox 
            sx={{ 
              width: 496,
              height: 204,
              transform: 'rotate(0deg)',
              opacity: 1,
              alignSelf: 'flex-start',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              margin: '0 auto'
            }}
          >
            <DHBox
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                gap: '16px'
              }}
            >
              {/* Box 1 - Dark Sky Portal */}
              <DHBox
                sx={{
                  width: '100%',
                  padding: '12px 12px 12px 0',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginLeft: 0
                }}
              >
                <DHTypography
                  variant="headline-medium"
                  sx={{
                    fontFamily: 'Artifakt Legend, sans-serif',
                    fontWeight: 800,
                    fontSize: '26px',
                    lineHeight: '120%',
                    letterSpacing: '0%',
                    color: 'ink-on-background',
                    textAlign: 'left'
                  }}
                >
                  Dark Sky Portal
                </DHTypography>
              </DHBox>

              {/* Box 2 - Sign In Button */}
              <DHBox
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingLeft: 0,
                  marginLeft: 0
                }}
              >
                <DHButton
                  variant="contained"
                  color="primary"
                  onClick={handleLogin}
                  disabled={loading || isAuthenticating}
                  className="dhig-button-contained dhig-button-primary"
                  sx={{
                    width: 496,
                    height: 40,
                    borderRadius: '4px',
                    padding: '8px 20px',
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiButton-startIcon': {
                      display: 'flex !important',
                      alignItems: 'center',
                      marginRight: '8px',
                      '& .DhigCircularProgress--background .MuiCircularProgress-circle': {
                        stroke: 'rgba(255, 255, 255, 0.3) !important',
                      },
                      '& .DhigCircularProgress--foreground .MuiCircularProgress-circle': {
                        stroke: '#FFFFFF !important',
                      },
                      '& .MuiCircularProgress-root.DhigCircularProgress--background': {
                        color: 'rgba(255, 255, 255, 0.3) !important',
                      },
                      '& .MuiCircularProgress-root.DhigCircularProgress--foreground': {
                        color: '#FFFFFF !important',
                      },
                      '& svg circle.MuiCircularProgress-circleDeterminate': {
                        stroke: 'rgba(255, 255, 255, 0.3) !important',
                      },
                      '& svg circle.MuiCircularProgress-circleIndeterminate': {
                        stroke: '#FFFFFF !important',
                      }
                    },
                    '&:hover': {
                      width: 496,
                      height: 40,
                      padding: '8px 20px',
                      fontFamily: 'Artifakt Element, sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                  startIcon={
                    (loading || isAuthenticating) ? (
                      <DHCircularProgress
                        role="progressbar"
                        size="x-small"
                      />
                    ) : null
                  }
                >
                  {(loading || isAuthenticating) ? 'Signing in...' : 'Sign in'}
                </DHButton>
              </DHBox>

              {/* Box 3 - Divider */}
              <DHBox
                sx={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  alignSelf: 'flex-start',
                  marginLeft: 0
                }}
              />
              {/* Box 4 - Requires VPN access */}
              <DHBox
                sx={{
                  width: '100%',
                  padding: '8px 8px 8px 0',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginLeft: 0
                }}
              >
                <DHTypography
                  variant="body-copy-medium"
                  sx={{
                    fontFamily: 'Artifakt Element, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '150%',
                    letterSpacing: '0%',
                    color: 'ink-on-background',
                    opacity: 0.8,
                    textAlign: 'left'
                  }}
                >
                  Requires VPN connection
                </DHTypography>
              </DHBox>
            </DHBox>
          </DHBox>
        </DHCardContent>
      </DHCard>
    </DHBox>
  );
}

